import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { LayoutDashboardIcon, ShieldIcon, FileTextIcon, AlertCircleIcon, UsersIcon, SettingsIcon, SearchIcon, BellIcon, MoreVerticalIcon, CheckCircleIcon, ClockIcon, UploadCloudIcon, ArrowUpRightIcon, GraduationCapIcon, BriefcaseIcon, CarIcon, HeartPulseIcon, TrendingUpIcon, HomeIcon, StethoscopeIcon, SmartphoneIcon, XIcon, ChevronDownIcon, MenuIcon } from 'lucide-react';
import { formatNaira, formatDate } from '../utils/format';
export function AdminPanel() {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // Policies State
  const [policyFilter, setPolicyFilter] = useState('All');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedPolicy, setSelectedPolicy] = useState<string | null>(null);
  // Claims State
  const [claimFilter, setClaimFilter] = useState('All');
  const sidebarItems = [{
    id: 'dashboard',
    label: 'Dashboard',
    icon: LayoutDashboardIcon
  }, {
    id: 'products',
    label: 'Product Catalogue',
    icon: ShieldIcon
  }, {
    id: 'policies',
    label: 'Policies & Certs',
    icon: FileTextIcon
  }, {
    id: 'claims',
    label: 'Claims Management',
    icon: AlertCircleIcon
  }, {
    id: 'users',
    label: 'Users',
    icon: UsersIcon
  }, {
    id: 'settings',
    label: 'Settings',
    icon: SettingsIcon
  }];
  const stats = [{
    label: 'Total Active Policies',
    value: '12,450',
    change: '+12%',
    positive: true
  }, {
    label: 'Premium Collected (MTD)',
    value: formatNaira(45200000),
    change: '+8.5%',
    positive: true
  }, {
    label: 'Active Claims',
    value: '142',
    change: '-3%',
    positive: true
  }, {
    label: 'Pending Registrations',
    value: '89',
    change: '+15%',
    positive: false
  }];
  const recentClaims = [{
    id: 'CLM-8492',
    user: 'Adaeze Okonkwo',
    product: 'Motor Vehicle',
    date: '2026-03-12',
    amount: 500000,
    status: 'Under Review',
    underwriter: 'AXA Mansard'
  }, {
    id: 'CLM-8491',
    user: 'Chinedu Eze',
    product: 'Health Insurance',
    date: '2026-03-11',
    amount: 45000,
    status: 'Submitted',
    underwriter: 'Hygeia HMO'
  }, {
    id: 'CLM-7102',
    user: 'Adaeze Okonkwo',
    product: 'Device Insurance',
    date: '2026-02-20',
    amount: 120000,
    status: 'Settled',
    underwriter: 'Cornerstone'
  }];
  const pendingPolicies = [{
    id: 'POL-9921',
    user: 'Adaeze Okonkwo',
    product: 'Device Insurance',
    underwriter: 'Cornerstone',
    date: '2026-03-15',
    premium: 500
  }, {
    id: 'POL-9920',
    user: 'Oluwaseun Ade',
    product: 'Child Education',
    underwriter: 'Leadway',
    date: '2026-03-14',
    premium: 25000
  }];
  // Products Data
  const productsData = [{
    id: 1,
    name: 'Child Education',
    icon: GraduationCapIcon,
    color: 'text-emerald-600 bg-emerald-100',
    underwriter: 'Leadway Assurance',
    premium: '₦5,000 - ₦50,000/mo',
    status: 'Active',
    plans: 3
  }, {
    id: 2,
    name: 'Job Loss Cover',
    icon: BriefcaseIcon,
    color: 'text-blue-600 bg-blue-100',
    underwriter: 'Anchor Insurance',
    premium: '₦2,500 - ₦15,000/mo',
    status: 'Active',
    plans: 3
  }, {
    id: 3,
    name: 'Motor Vehicle',
    icon: CarIcon,
    color: 'text-teal-600 bg-teal-100',
    underwriter: 'AXA Mansard',
    premium: '₦15,000 - ₦200,000/yr',
    status: 'Active',
    plans: 3
  }, {
    id: 4,
    name: 'Life & Disability',
    icon: HeartPulseIcon,
    color: 'text-rose-600 bg-rose-100',
    underwriter: 'Leadway Assurance',
    premium: '₦3,000 - ₦100,000/mo',
    status: 'Active',
    plans: 3
  }, {
    id: 5,
    name: 'General Annuity',
    icon: TrendingUpIcon,
    color: 'text-purple-600 bg-purple-100',
    underwriter: 'Stanbic IBTC',
    premium: '₦10,000 - ₦500,000/mo',
    status: 'Active',
    plans: 2
  }, {
    id: 6,
    name: 'Home Insurance',
    icon: HomeIcon,
    color: 'text-amber-600 bg-amber-100',
    underwriter: 'Cornerstone Insurance',
    premium: '₦4,000 - ₦80,000/yr',
    status: 'Active',
    plans: 3
  }, {
    id: 7,
    name: 'Health Insurance',
    icon: StethoscopeIcon,
    color: 'text-cyan-600 bg-cyan-100',
    underwriter: 'Hygeia HMO',
    premium: '₦8,000 - ₦150,000/qtr',
    status: 'Active',
    plans: 3
  }, {
    id: 8,
    name: 'Device Insurance',
    icon: SmartphoneIcon,
    color: 'text-slate-700 bg-slate-200',
    underwriter: 'Cornerstone Insurance',
    premium: '₦500 - ₦5,000/mo',
    status: 'Active',
    plans: 2
  }];
  // Policies Data
  const policiesData = [{
    id: 'SKY-2026-001',
    customer: 'Adaeze Okonkwo',
    product: 'Child Education',
    underwriter: 'Leadway',
    date: '2026-03-10',
    status: 'Registered'
  }, {
    id: 'SKY-2026-002',
    customer: 'Adaeze Okonkwo',
    product: 'Motor Vehicle',
    underwriter: 'AXA Mansard',
    date: '2026-03-12',
    status: 'Registered'
  }, {
    id: 'SKY-2026-003',
    customer: 'Adaeze Okonkwo',
    product: 'Device Insurance',
    underwriter: 'Cornerstone',
    date: '2026-03-14',
    status: 'Pending'
  }, {
    id: 'SKY-2026-004',
    customer: 'Chidi Eze',
    product: 'Job Loss Cover',
    underwriter: 'Anchor',
    date: '2026-03-14',
    status: 'Forwarded'
  }, {
    id: 'SKY-2026-005',
    customer: 'Ngozi Adeyemi',
    product: 'Health Insurance',
    underwriter: 'Hygeia',
    date: '2026-03-15',
    status: 'Pending'
  }, {
    id: 'SKY-2026-006',
    customer: 'Emeka Nwosu',
    product: 'General Annuity',
    underwriter: 'Stanbic IBTC',
    date: '2026-03-15',
    status: 'Forwarded'
  }, {
    id: 'SKY-2026-007',
    customer: 'Fatima Bello',
    product: 'Home Insurance',
    underwriter: 'Cornerstone',
    date: '2026-03-15',
    status: 'Pending'
  }];
  const filteredPolicies = policyFilter === 'All' ? policiesData : policiesData.filter(p => p.status === policyFilter);
  // Claims Data (Stateful for updates)
  const [claimsData, setClaimsData] = useState([{
    id: 'CLM-8492',
    customer: 'Adaeze Okonkwo',
    product: 'Motor Vehicle',
    underwriter: 'AXA Mansard',
    amount: 500000,
    date: '2026-03-12',
    status: 'Under Review'
  }, {
    id: 'CLM-8501',
    customer: 'Chidi Eze',
    product: 'Job Loss',
    underwriter: 'Anchor Insurance',
    amount: 180000,
    date: '2026-03-13',
    status: 'Submitted'
  }, {
    id: 'CLM-8510',
    customer: 'Ngozi Adeyemi',
    product: 'Health',
    underwriter: 'Hygeia HMO',
    amount: 45000,
    date: '2026-03-14',
    status: 'Approved'
  }, {
    id: 'CLM-7102',
    customer: 'Adaeze Okonkwo',
    product: 'Device Insurance',
    underwriter: 'Cornerstone',
    amount: 120000,
    date: '2026-02-20',
    status: 'Settled'
  }, {
    id: 'CLM-8520',
    customer: 'Emeka Nwosu',
    product: 'Motor Vehicle',
    underwriter: 'AXA Mansard',
    amount: 350000,
    date: '2026-03-15',
    status: 'Submitted'
  }]);
  const filteredClaims = claimFilter === 'All' ? claimsData : claimsData.filter(c => c.status === claimFilter);
  const handleClaimStatusUpdate = (id: string, newStatus: string) => {
    setClaimsData(claimsData.map(c => c.id === id ? {
      ...c,
      status: newStatus
    } : c));
  };
  const renderContent = () => {
    switch (activeTab) {
      case 'dashboard':
        return <div className="space-y-6" data-id="element-57">
            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-58">
              {stats.map((stat, idx) => <div key={idx} className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm" data-id="element-59">
                  <p className="text-sm font-medium text-gray-500 mb-2" data-id="element-60">
                    {stat.label}
                  </p>
                  <div className="flex items-end justify-between" data-id="element-61">
                    <h3 className="text-2xl font-bold text-[#1B3B36]" data-id="element-62">
                      {stat.value}
                    </h3>
                    <span className={`text-xs font-bold px-2 py-1 rounded-full ${stat.positive ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`} data-id="element-63">
                      {stat.change}
                    </span>
                  </div>
                </div>)}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-64">
              {/* Claims Overview */}
              <div className="lg:col-span-2 bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden" data-id="element-65">
                <div className="px-6 py-5 border-b border-gray-100 flex flex-wrap gap-3 justify-between items-center" data-id="element-66">
                  <h3 className="font-bold text-[#1B3B36]" data-id="element-67">Recent Claims</h3>
                  <button onClick={() => setActiveTab('claims')} className="text-sm font-medium text-teal-600 hover:text-teal-700" data-id="element-68">
                    View All
                  </button>
                </div>
                <div className="overflow-x-auto" data-id="element-69">
                  <table className="w-full text-left border-collapse" data-id="element-70">
                    <thead data-id="element-71">
                      <tr className="bg-gray-50/50 text-xs uppercase tracking-wider text-gray-500 font-semibold" data-id="element-72">
                        <th className="px-6 py-3" data-id="element-73">Claim ID</th>
                        <th className="px-6 py-3" data-id="element-74">User & Product</th>
                        <th className="px-6 py-3" data-id="element-75">Underwriter</th>
                        <th className="px-6 py-3" data-id="element-76">Amount</th>
                        <th className="px-6 py-3" data-id="element-77">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100" data-id="element-78">
                      {recentClaims.map(claim => <tr key={claim.id} className="hover:bg-gray-50 transition-colors" data-id="element-79">
                          <td className="px-6 py-4 text-sm font-medium text-[#1B3B36]" data-id="element-80">
                            {claim.id}
                          </td>
                          <td className="px-6 py-4" data-id="element-81">
                            <div className="text-sm font-bold text-[#1B3B36]" data-id="element-82">
                              {claim.user}
                            </div>
                            <div className="text-xs text-gray-500" data-id="element-83">
                              {claim.product}
                            </div>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-600" data-id="element-84">
                            {claim.underwriter}
                          </td>
                          <td className="px-6 py-4 text-sm font-bold text-[#1B3B36]" data-id="element-85">
                            {formatNaira(claim.amount)}
                          </td>
                          <td className="px-6 py-4" data-id="element-86">
                            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${claim.status === 'Settled' ? 'bg-emerald-100 text-emerald-700' : claim.status === 'Under Review' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'}`} data-id="element-87">
                              {claim.status === 'Settled' ? <CheckCircleIcon className="w-3.5 h-3.5" data-id="element-88" /> : <ClockIcon className="w-3.5 h-3.5" data-id="element-89" />}
                              {claim.status}
                            </span>
                          </td>
                        </tr>)}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Action Required: Pending Policies */}
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden flex flex-col" data-id="element-90">
                <div className="px-6 py-5 border-b border-gray-100" data-id="element-91">
                  <h3 className="font-bold text-[#1B3B36] flex items-center gap-2" data-id="element-92">
                    <AlertCircleIcon className="w-5 h-5 text-amber-500" data-id="element-93" />{' '}
                    Action Required
                  </h3>
                  <p className="text-xs text-gray-500 mt-1" data-id="element-94">
                    Pending underwriter registration
                  </p>
                </div>
                <div className="p-6 flex-1 flex flex-col gap-4" data-id="element-95">
                  {pendingPolicies.map(policy => <div key={policy.id} className="border border-gray-100 rounded-xl p-4 hover:border-teal-200 transition-colors" data-id="element-96">
                      <div className="flex justify-between items-start mb-2" data-id="element-97">
                        <div data-id="element-98">
                          <h4 className="font-bold text-[#1B3B36] text-sm" data-id="element-99">
                            {policy.user}
                          </h4>
                          <p className="text-xs text-gray-500" data-id="element-100">
                            {policy.product}
                          </p>
                        </div>
                        <span className="text-xs font-bold text-[#1B3B36]" data-id="element-101">
                          {formatNaira(policy.premium)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-50" data-id="element-102">
                        <span className="text-xs text-gray-500" data-id="element-103">
                          To: {policy.underwriter}
                        </span>
                        <button className="text-xs font-bold text-teal-600 flex items-center gap-1 hover:text-teal-800" data-id="element-104">
                          Export Data <ArrowUpRightIcon className="w-3 h-3" data-id="element-105" />
                        </button>
                      </div>
                    </div>)}
                  <button onClick={() => setActiveTab('policies')} className="mt-auto w-full py-2.5 bg-gray-50 hover:bg-gray-100 text-sm font-bold text-[#1B3B36] rounded-xl transition-colors" data-id="element-106">
                    View All Pending
                  </button>
                </div>
              </div>
            </div>
          </div>;
      case 'products':
        return <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden" data-id="element-107">
            <div className="px-6 py-5 border-b border-gray-100 flex flex-wrap gap-3 justify-between items-center bg-gray-50/50" data-id="element-108">
              <h3 className="font-bold text-[#1B3B36]" data-id="element-109">Product Catalogue</h3>
              <button className="px-4 py-2 bg-[#1B3B36] text-white rounded-lg text-sm font-medium shadow-sm hover:bg-[#0F2B27] transition-colors" data-id="element-110">
                Add New Product
              </button>
            </div>
            <div className="overflow-x-auto" data-id="element-111">
              <table className="w-full text-left border-collapse" data-id="element-112">
                <thead data-id="element-113">
                  <tr className="bg-gray-50/50 text-xs uppercase tracking-wider text-gray-500 font-semibold" data-id="element-114">
                    <th className="px-6 py-3" data-id="element-115">Product Name</th>
                    <th className="px-6 py-3" data-id="element-116">Underwriter</th>
                    <th className="px-6 py-3" data-id="element-117">Premium Range</th>
                    <th className="px-6 py-3" data-id="element-118">Status</th>
                    <th className="px-6 py-3" data-id="element-119">Plans</th>
                    <th className="px-6 py-3 text-right" data-id="element-120">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100" data-id="element-121">
                  {productsData.map(product => <tr key={product.id} className="hover:bg-gray-50 transition-colors" data-id="element-122">
                      <td className="px-6 py-4" data-id="element-123">
                        <div className="flex items-center gap-3" data-id="element-124">
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${product.color}`} data-id="element-125">
                            <product.icon className="w-4 h-4" data-id="element-126" />
                          </div>
                          <span className="font-bold text-[#1B3B36] text-sm" data-id="element-127">
                            {product.name}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600" data-id="element-128">
                        {product.underwriter}
                      </td>
                      <td className="px-6 py-4 text-sm font-medium text-[#1B3B36]" data-id="element-129">
                        {product.premium}
                      </td>
                      <td className="px-6 py-4" data-id="element-130">
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700" data-id="element-131">
                          <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" data-id="element-132"></div>
                          {product.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600" data-id="element-133">
                        {product.plans} plans
                      </td>
                      <td className="px-6 py-4 text-right space-x-3" data-id="element-134">
                        <button className="text-sm font-medium text-teal-600 hover:text-teal-800" data-id="element-135">
                          Edit
                        </button>
                        <button className="text-sm font-medium text-gray-500 hover:text-gray-700" data-id="element-136">
                          View Plans
                        </button>
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
          </div>;
      case 'claims':
        return <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden" data-id="element-137">
            <div className="px-6 py-5 border-b border-gray-100 flex flex-wrap gap-3 justify-between items-center bg-gray-50/50" data-id="element-138">
              <h3 className="font-bold text-[#1B3B36]" data-id="element-139">Claims Management</h3>
              <button className="px-4 py-2 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-600 shadow-sm hover:bg-gray-50" data-id="element-140">
                Export CSV
              </button>
            </div>

            {/* Tabs */}
            <div className="px-6 py-3 border-b border-gray-100 flex gap-2 overflow-x-auto" data-id="element-141">
              {['All', 'Submitted', 'Under Review', 'Approved', 'Settled', 'Rejected'].map(tab => <button key={tab} onClick={() => setClaimFilter(tab)} className={`px-4 py-1.5 text-sm font-medium rounded-full transition-colors whitespace-nowrap ${claimFilter === tab ? 'bg-[#1B3B36] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-id="element-142">
                  {tab}
                </button>)}
            </div>

            <div className="overflow-x-auto" data-id="element-143">
              <table className="w-full text-left border-collapse" data-id="element-144">
                <thead data-id="element-145">
                  <tr className="bg-gray-50/50 text-xs uppercase tracking-wider text-gray-500 font-semibold" data-id="element-146">
                    <th className="px-6 py-3" data-id="element-147">Claim ID</th>
                    <th className="px-6 py-3" data-id="element-148">Customer</th>
                    <th className="px-6 py-3" data-id="element-149">Product / Underwriter</th>
                    <th className="px-6 py-3" data-id="element-150">Amount</th>
                    <th className="px-6 py-3" data-id="element-151">Date Filed</th>
                    <th className="px-6 py-3" data-id="element-152">Status</th>
                    <th className="px-6 py-3 text-right" data-id="element-153">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100" data-id="element-154">
                  {filteredClaims.map(claim => <tr key={claim.id} className="hover:bg-gray-50 transition-colors" data-id="element-155">
                      <td className="px-6 py-4 text-sm font-bold text-[#1B3B36]" data-id="element-156">
                        {claim.id}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-800" data-id="element-157">
                        {claim.customer}
                      </td>
                      <td className="px-6 py-4" data-id="element-158">
                        <div className="text-sm font-medium text-[#1B3B36]" data-id="element-159">
                          {claim.product}
                        </div>
                        <div className="text-xs text-gray-500" data-id="element-160">
                          {claim.underwriter}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm font-bold text-[#1B3B36]" data-id="element-161">
                        {formatNaira(claim.amount)}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600" data-id="element-162">
                        {formatDate(claim.date)}
                      </td>
                      <td className="px-6 py-4" data-id="element-163">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium 
                          ${claim.status === 'Submitted' ? 'bg-blue-100 text-blue-700' : claim.status === 'Under Review' ? 'bg-amber-100 text-amber-700' : claim.status === 'Approved' ? 'bg-teal-100 text-teal-700' : claim.status === 'Settled' ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`} data-id="element-164">
                          <div className={`w-1.5 h-1.5 rounded-full 
                            ${claim.status === 'Submitted' ? 'bg-blue-500' : claim.status === 'Under Review' ? 'bg-amber-500' : claim.status === 'Approved' ? 'bg-teal-500' : claim.status === 'Settled' ? 'bg-emerald-500' : 'bg-rose-500'}`} data-id="element-165"></div>
                          {claim.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right space-x-3 whitespace-nowrap" data-id="element-166">
                        <select value={claim.status} onChange={e => handleClaimStatusUpdate(claim.id, e.target.value)} className="text-sm font-medium text-gray-600 bg-transparent border-none focus:ring-0 cursor-pointer hover:text-[#1B3B36]" data-id="element-167">
                          <option value="Submitted" data-id="element-168">Submitted</option>
                          <option value="Under Review" data-id="element-169">Under Review</option>
                          <option value="Approved" data-id="element-170">Approved</option>
                          <option value="Settled" data-id="element-171">Settled</option>
                          <option value="Rejected" data-id="element-172">Rejected</option>
                        </select>
                        <button className="text-sm font-medium text-teal-600 hover:text-teal-800" data-id="element-173">
                          Forward
                        </button>
                        <button className="text-sm font-medium text-gray-500 hover:text-gray-700" data-id="element-174">
                          Docs
                        </button>
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
          </div>;
      case 'policies':
        return <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden relative" data-id="element-175">
            <div className="px-6 py-5 border-b border-gray-100 flex flex-wrap gap-3 justify-between items-center bg-gray-50/50" data-id="element-176">
              <h3 className="font-bold text-[#1B3B36]" data-id="element-177">
                Policy Registration Tracker
              </h3>
            </div>

            {/* Tabs */}
            <div className="px-6 py-3 border-b border-gray-100 flex gap-2 overflow-x-auto" data-id="element-178">
              {['All', 'Pending', 'Forwarded', 'Registered'].map(tab => <button key={tab} onClick={() => setPolicyFilter(tab)} className={`px-4 py-1.5 text-sm font-medium rounded-full transition-colors whitespace-nowrap ${policyFilter === tab ? 'bg-[#1B3B36] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-id="element-179">
                  {tab}
                </button>)}
            </div>

            <div className="overflow-x-auto" data-id="element-180">
              <table className="w-full text-left border-collapse" data-id="element-181">
                <thead data-id="element-182">
                  <tr className="bg-gray-50/50 text-xs uppercase tracking-wider text-gray-500 font-semibold" data-id="element-183">
                    <th className="px-6 py-3" data-id="element-184">Policy No</th>
                    <th className="px-6 py-3" data-id="element-185">Customer</th>
                    <th className="px-6 py-3" data-id="element-186">Product</th>
                    <th className="px-6 py-3" data-id="element-187">Underwriter</th>
                    <th className="px-6 py-3" data-id="element-188">Date Sold</th>
                    <th className="px-6 py-3" data-id="element-189">Status</th>
                    <th className="px-6 py-3 text-right" data-id="element-190">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100" data-id="element-191">
                  {filteredPolicies.map(policy => <tr key={policy.id} className="hover:bg-gray-50 transition-colors" data-id="element-192">
                      <td className="px-6 py-4 text-sm font-bold text-[#1B3B36]" data-id="element-193">
                        {policy.id}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-800" data-id="element-194">
                        {policy.customer}
                      </td>
                      <td className="px-6 py-4 text-sm font-medium text-[#1B3B36]" data-id="element-195">
                        {policy.product}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600" data-id="element-196">
                        {policy.underwriter}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600" data-id="element-197">
                        {formatDate(policy.date)}
                      </td>
                      <td className="px-6 py-4" data-id="element-198">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium 
                          ${policy.status === 'Pending' ? 'bg-amber-100 text-amber-700' : policy.status === 'Forwarded' ? 'bg-blue-100 text-blue-700' : 'bg-emerald-100 text-emerald-700'}`} data-id="element-199">
                          {policy.status === 'Registered' && <CheckCircleIcon className="w-3.5 h-3.5" data-id="element-200" />}
                          {policy.status !== 'Registered' && <div className={`w-1.5 h-1.5 rounded-full ${policy.status === 'Pending' ? 'bg-amber-500' : 'bg-blue-500'}`} data-id="element-201"></div>}
                          {policy.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right" data-id="element-202">
                        {policy.status === 'Pending' && <button className="text-sm font-bold text-teal-600 hover:text-teal-800" data-id="element-203">
                            Forward to Underwriter
                          </button>}
                        {policy.status === 'Forwarded' && <button onClick={() => {
                      setSelectedPolicy(policy.id);
                      setShowUploadModal(true);
                    }} className="text-sm font-bold text-[#E8734A] hover:text-[#d4663f]" data-id="element-204">
                            Upload Certificate
                          </button>}
                        {policy.status === 'Registered' && <button className="text-sm font-medium text-gray-500 hover:text-gray-700" data-id="element-205">
                            View Certificate
                          </button>}
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>

            {/* Upload Modal */}
            {showUploadModal && <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-206">
                <div className="absolute inset-0 bg-[#1B3B36]/40 backdrop-blur-sm" onClick={() => setShowUploadModal(false)} data-id="element-207"></div>
                <motion.div initial={{
              opacity: 0,
              scale: 0.95,
              y: 20
            }} animate={{
              opacity: 1,
              scale: 1,
              y: 0
            }} className="bg-white rounded-2xl shadow-xl w-full max-w-md relative z-10 overflow-hidden" data-id="element-208">
                  <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center" data-id="element-209">
                    <h3 className="font-bold text-[#1B3B36] text-lg" data-id="element-210">
                      Upload Final Certificate
                    </h3>
                    <button onClick={() => setShowUploadModal(false)} className="text-gray-400 hover:text-gray-600" data-id="element-211">
                      <XIcon className="w-5 h-5" data-id="element-212" />
                    </button>
                  </div>
                  <div className="p-6" data-id="element-213">
                    <p className="text-sm text-gray-500 mb-4" data-id="element-214">
                      Uploading certificate for policy{' '}
                      <span className="font-bold text-[#1B3B36]" data-id="element-215">
                        {selectedPolicy}
                      </span>
                      . This will be instantly available in the customer's
                      Document Vault.
                    </p>

                    <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:bg-gray-50 transition-colors cursor-pointer mb-6" data-id="element-216">
                      <UploadCloudIcon className="w-10 h-10 text-teal-400 mx-auto mb-3" data-id="element-217" />
                      <p className="text-sm font-bold text-[#1B3B36]" data-id="element-218">
                        Click to upload or drag and drop
                      </p>
                      <p className="text-xs text-gray-500 mt-1" data-id="element-219">
                        PDF documents only (max 10MB)
                      </p>
                    </div>

                    <button onClick={() => setShowUploadModal(false)} className="w-full bg-emerald-500 hover:bg-emerald-600 text-white font-bold py-3 rounded-xl transition-colors shadow-sm" data-id="element-220">
                      Upload & Notify Customer
                    </button>
                  </div>
                </motion.div>
              </div>}
          </div>;
      default:
        return <div className="flex items-center justify-center h-64 text-gray-400" data-id="element-221">
            Select a module from the sidebar.
          </div>;
    }
  };
  return <div className="min-h-screen bg-gray-50 flex w-full font-sans" data-id="element-222">
      {/* Mobile Sidebar Overlay */}
      <AnimatePresence data-id="element-223">
        {sidebarOpen && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={() => setSidebarOpen(false)} className="fixed inset-0 bg-[#1B3B36]/50 backdrop-blur-sm z-40 md:hidden" data-id="element-224" />

            <motion.aside initial={{
          x: '-100%'
        }} animate={{
          x: 0
        }} exit={{
          x: '-100%'
        }} transition={{
          type: 'spring',
          damping: 25,
          stiffness: 200
        }} className="fixed inset-y-0 left-0 w-64 bg-[#1B3B36] text-white z-50 flex flex-col md:hidden shadow-2xl" data-id="element-225">
              <div className="p-6 flex justify-between items-start" data-id="element-226">
                <div data-id="element-227">
                  <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/txx1kwrkrvwwngiwspm5ax/2e7e9641-86a1-4286-bf63-6418efd76835/IMG_4106.png" alt="Skydd" className="h-7 w-auto mb-1" data-id="element-228" />

                  <div className="text-xs text-teal-300 uppercase tracking-wider font-semibold" data-id="element-229">
                    Admin Portal
                  </div>
                </div>
                <button onClick={() => setSidebarOpen(false)} className="text-teal-300 hover:text-white p-1" data-id="element-230">
                  <XIcon className="w-5 h-5" data-id="element-231" />
                </button>
              </div>

              <nav className="flex-1 px-4 py-4 space-y-1 overflow-y-auto" data-id="element-232">
                {sidebarItems.map(item => {
              const isActive = activeTab === item.id;
              return <button key={item.id} onClick={() => {
                setActiveTab(item.id);
                setSidebarOpen(false);
              }} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors text-sm font-medium ${isActive ? 'bg-teal-600 text-white shadow-md' : 'text-teal-200 hover:bg-white/5 hover:text-white'}`} data-id="element-233">
                      <item.icon className={`w-5 h-5 ${isActive ? 'text-white' : 'text-teal-400'}`} data-id="element-234" />

                      {item.label}
                    </button>;
            })}
              </nav>

              <div className="p-4 m-4 bg-white/5 rounded-xl border border-white/10" data-id="element-235">
                <div className="flex items-center gap-3" data-id="element-236">
                  <div className="w-10 h-10 rounded-full bg-teal-500 flex items-center justify-center font-bold" data-id="element-237">
                    SA
                  </div>
                  <div className="text-left" data-id="element-238">
                    <p className="text-sm font-bold" data-id="element-239">System Admin</p>
                    <p className="text-xs text-teal-300" data-id="element-240">Operations Team</p>
                  </div>
                </div>
              </div>
            </motion.aside>
          </>}
      </AnimatePresence>

      {/* Desktop Sidebar */}
      <aside className="w-64 bg-[#1B3B36] text-white hidden md:flex flex-col" data-id="element-241">
        <div className="p-6" data-id="element-242">
          <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/txx1kwrkrvwwngiwspm5ax/2e7e9641-86a1-4286-bf63-6418efd76835/IMG_4106.png" alt="Skydd" className="h-7 w-auto mb-1" data-id="element-243" />

          <div className="text-xs text-teal-300 uppercase tracking-wider font-semibold" data-id="element-244">
            Admin Portal
          </div>
        </div>

        <nav className="flex-1 px-4 py-4 space-y-1" data-id="element-245">
          {sidebarItems.map(item => {
          const isActive = activeTab === item.id;
          return <button key={item.id} onClick={() => setActiveTab(item.id)} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors text-sm font-medium ${isActive ? 'bg-teal-600 text-white shadow-md' : 'text-teal-200 hover:bg-white/5 hover:text-white'}`} data-id="element-246">
                <item.icon className={`w-5 h-5 ${isActive ? 'text-white' : 'text-teal-400'}`} data-id="element-247" />

                {item.label}
              </button>;
        })}
        </nav>

        <div className="p-4 m-4 bg-white/5 rounded-xl border border-white/10" data-id="element-248">
          <div className="flex items-center gap-3" data-id="element-249">
            <div className="w-10 h-10 rounded-full bg-teal-500 flex items-center justify-center font-bold" data-id="element-250">
              SA
            </div>
            <div className="text-left" data-id="element-251">
              <p className="text-sm font-bold" data-id="element-252">System Admin</p>
              <p className="text-xs text-teal-300" data-id="element-253">Operations Team</p>
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 flex flex-col min-w-0 overflow-hidden" data-id="element-254">
        {/* Top Header */}
        <header className="bg-white border-b border-gray-200 px-4 md:px-8 py-4 flex items-center justify-between sticky top-0 z-10" data-id="element-255">
          <div className="flex items-center gap-4 flex-1" data-id="element-256">
            <button onClick={() => setSidebarOpen(true)} className="p-2 -ml-2 text-gray-500 hover:text-gray-700 md:hidden" data-id="element-257">
              <MenuIcon className="w-6 h-6" data-id="element-258" />
            </button>
            <div className="relative w-full max-w-md hidden sm:block" data-id="element-259">
              <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" data-id="element-260" />
              <input type="text" placeholder="Search policies, claims, or users..." className="w-full bg-gray-50 border border-gray-200 rounded-lg py-2 pl-10 pr-4 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500" data-id="element-261" />
            </div>
          </div>
          <div className="flex items-center gap-2 md:gap-4" data-id="element-262">
            <button className="p-2 text-gray-400 hover:text-gray-600 transition-colors sm:hidden" data-id="element-263">
              <SearchIcon className="w-5 h-5" data-id="element-264" />
            </button>
            <button className="relative p-2 text-gray-400 hover:text-gray-600 transition-colors" data-id="element-265">
              <BellIcon className="w-5 h-5" data-id="element-266" />
              <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-rose-500 rounded-full border-2 border-white" data-id="element-267"></span>
            </button>
            <button className="p-2 text-gray-400 hover:text-gray-600 transition-colors" data-id="element-268">
              <SettingsIcon className="w-5 h-5" data-id="element-269" />
            </button>
          </div>
        </header>

        {/* Page Content */}
        <div className="flex-1 overflow-y-auto p-4 md:p-8" data-id="element-270">
          <div className="max-w-7xl mx-auto" data-id="element-271">
            <div className="mb-6 md:mb-8" data-id="element-272">
              <h1 className="text-2xl font-bold text-[#1B3B36]" data-id="element-273">
                {sidebarItems.find(i => i.id === activeTab)?.label}
              </h1>
              <p className="text-gray-500 text-sm mt-1" data-id="element-274">
                Manage platform operations and underwriter liaisons.
              </p>
            </div>

            <motion.div key={activeTab} initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.2
          }} data-id="element-275">
              {renderContent()}
            </motion.div>
          </div>
        </div>
      </main>
    </div>;
}